import { formatPastRaceDate } from "@tvg/formatter/dates";
import { useEffect, useState } from "react";
import { useQuery, ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { useSelector } from "react-redux";

import { BetBasicInfo } from "@tvg/ts-types/Bet";
import { RaceProgram } from "@tvg/ts-types/Race";
import { isAccountCompliantSelector } from "@tvg/sh-utils/sessionUtils";
import { getAccountNumber } from "@urp/store-selectors";
import useTrackInfo from "./useTrackInfo";
import useRaceWagersSubscription from "./useRaceWagersSubscription";
import queryHelper from "../graphql/options.graph";
import { GET_RACE_WAGERS } from "../graphql/queries/RaceWagersQuery";
import { RaceWagerSummary } from "../types";

const useCurrentRaceWagers = ({
  race,
  behgClient
}: {
  race: RaceProgram;
  behgClient: ApolloClient<NormalizedCacheObject>;
}) => {
  const [raceWagers, setRaceWagers] = useState<Array<BetBasicInfo>>([]);
  const [raceWagersSummary, setRaceWagersSummary] = useState<RaceWagerSummary>({
    totalBets: 0,
    totalAmount: 0,
    totalGambledCount: 0,
    totalGambledAmount: 0
  });
  const isAccountCompliant = useSelector(isAccountCompliantSelector);

  const accountId = useSelector(getAccountNumber);
  let { track: trackCode, raceNumber } = useTrackInfo();

  trackCode = race.track?.trackCode || trackCode;
  raceNumber = race.raceNumber || raceNumber;

  // request race wagers data
  const { data, loading, subscribeToMore } = useQuery(
    GET_RACE_WAGERS,
    queryHelper.apolloOptionsRaceWagers({
      accountId: +accountId,
      trackCode,
      raceNumber: +raceNumber,
      behgClient,
      isAccountCompliant
    })
  );

  // subscribe for changes
  useRaceWagersSubscription(
    +accountId,
    trackCode,
    +raceNumber,
    subscribeToMore
  );

  // select race wagers to load
  useEffect(() => {
    const { wagers = [], totals } = queryHelper.getUserRaceBets(
      data,
      trackCode,
      +raceNumber,
      formatPastRaceDate(new Date())
    );

    setRaceWagers(wagers);
    setRaceWagersSummary(totals);
  }, [JSON.stringify(data?.wagerHistory)]);

  return { trackCode, raceNumber, raceWagersSummary, raceWagers, loading };
};

export default useCurrentRaceWagers;
